import type { GridsterEngine } from './gridster-engine';
import { GridsterDraggable } from './gridster-draggable';
import { GridsterResizable } from './gridster-resizable';
import { GridsterItemConfig, GridsterItemSize, GridsterResizableHandles } from '../types';
import { sameLayout } from './gridster-utils';

export class GridsterItemInstance {
  item: GridsterItemConfig;
  $item: GridsterItemConfig;
  notPlaced = false;
  init = false;
  isMoving = false;
  isResizing = false;
  top = 0;
  left = 0;
  width = 0;
  height = 0;
  drag: GridsterDraggable;
  resize: GridsterResizable;
  onSizeChange?: (size: GridsterItemSize) => void;

  constructor(
    private gridster: GridsterEngine,
    public el: HTMLElement,
    item: GridsterItemConfig
  ) {
    this.item = item;
    this.$item = { ...item };
    this.drag = new GridsterDraggable(this, gridster);
    this.resize = new GridsterResizable(this, gridster);
  }

  destroy(): void {
    this.drag.destroy();
    this.resize.destroy();
    this.onSizeChange = undefined;
  }

  setSize(): void {
    this.el.style.display = this.notPlaced ? 'none' : 'block';
    this.gridster.gridRenderer.updateItem(this.el, this.$item);
    this.updateItemSize();
  }

  updateItemSize(): void {
    const options = this.gridster.options;
    const $item = this.$item;
    this.top = $item.y * this.gridster.curRowHeight;
    this.left = $item.x * this.gridster.curColWidth;
    const width = $item.cols * this.gridster.curColWidth - options.margin;
    const height = $item.rows * this.gridster.curRowHeight - options.margin;

    if (!this.init && width > 0 && height > 0) {
      this.init = true;
    }

    if (width !== this.width || height !== this.height) {
      this.width = width;
      this.height = height;
      this.onSizeChange?.({ x: this.left, y: this.top, width, height });
    }
  }

  updateConfig(item: GridsterItemConfig): void {
    if (item === this.item) {
      return;
    }
    if (this.isMoving || this.isResizing) {
      return;
    }

    const previous = this.item;
    this.item = item;

    if (item.x < 0 || item.y < 0) {
      return;
    }

    const layoutChanged = !sameLayout(previous, item);
    this.$item = { ...item };

    if (layoutChanged) {
      this.gridster.calculateLayout();
    }
  }

  itemChanged(): void {
    this.gridster.itemChanged(this);
  }

  checkItemChanges(newValue: GridsterItemConfig, oldValue: GridsterItemConfig): boolean {
    if (sameLayout(newValue, oldValue)) {
      return false;
    }

    const $item = this.$item;
    if (this.gridster.checkCollision($item)) {
      $item.x = oldValue.x;
      $item.y = oldValue.y;
      $item.cols = oldValue.cols;
      $item.rows = oldValue.rows;
      this.setSize();
      return false;
    }

    this.item.x = $item.x;
    this.item.y = $item.y;
    this.item.cols = $item.cols;
    this.item.rows = $item.rows;
    this.gridster.calculateLayout();
    this.itemChanged();
    return true;
  }

  canBeDragged(): boolean {
    const options = this.gridster.options.draggable;
    return options.enabled && (this.$item.dragEnabled ?? true);
  }

  canBeResized(): boolean {
    const options = this.gridster.options.resizable;
    return options.enabled && (this.$item.resizeEnabled ?? true);
  }

  getResizableHandles(): Required<GridsterResizableHandles> {
    return {
      ...this.gridster.options.resizable.handles,
      ...this.$item.resizableHandles
    } as Required<GridsterResizableHandles>;
  }
}
